/**
 * <ActivityAnalysisNotice> — ce que Sam a lu dans le fichier déposé (molecule).
 *
 * Les encarts de `GenerateActivityModal` sous le champ Chapitre, à l'identique :
 *  - « Proposition en cours… » pendant l'analyse ;
 *  - « Sélectionné par Sam » quand elle a choisi le chapitre elle-même ;
 *  - « Sam a lu : « … » » + « Pas de correspondance en 4ème · Mathématiques — choisissez un
 *    chapitre. » quand rien ne correspond dans la classe et la matière en cours ;
 *  - la bascule vers une autre classe, avec ses deux mots : « Ignorer » / « Basculer » ;
 *  - « Pas de chapitre proposé. Choisissez-le vous-même. » en cas d'échec (le legacy disait « Suggestion AI
 *    indisponible », en anglais).
 *
 * Un encart, jamais deux : le legacy les gardait exclusifs par des conditions empilées, la règle
 * s'écrit ici une fois. Rien n'est dessiné à la main — `<AlertCard>` porte la surface teintée.
 */
import React from "react"

import { type UIActivityAnalysis } from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { AlertCard } from "../AlertCard"
import { type Voice } from "../../utils/voice"

/** Les deux voix de `GenerateActivityModal.js` (`TEXTS.student` / `TEXTS.parent`). */
const TEXTS: Record<Voice, { suggestionUnavailable: string; pickChapter: string; switchClass: string }> = {
    parent: {
        suggestionUnavailable: "Pas de chapitre proposé. Choisissez-le vous-même.",
        pickChapter: " — choisissez un chapitre.",
        switchClass: " — basculer ?",
    },
    student: {
        suggestionUnavailable: "Pas de chapitre proposé. Choisis-le toi-même.",
        // Le legacy écrit un tiret cadratin ; l'interface de l'élève n'en porte pas.
        pickChapter: " : choisis un chapitre.",
        switchClass: " : basculer ?",
    },
}

export type ActivityAnalysisNoticeProps = {
    analysis: UIActivityAnalysis
    /** La classe et la matière en cours — « Pas de correspondance en 4ème · Mathématiques ». */
    scopeLabel?: string
    /** Le chapitre est déjà choisi : les encarts « rien trouvé » se taisent. */
    hasChapter?: boolean
    onAcceptCrossClass?: () => void
    onDismissCrossClass?: () => void
    /** À qui l'encart parle. Défaut `"parent"`. */
    voice?: Voice
    testID?: string
}

export function ActivityAnalysisNotice(props: ActivityAnalysisNoticeProps) {
    const {
        analysis,
        scopeLabel,
        hasChapter = false,
        onAcceptCrossClass,
        onDismissCrossClass,
        voice = "parent",
        testID,
    } = props
    const texts = TEXTS[voice]

    if (analysis.status === "idle") return null

    if (analysis.status === "analyzing") {
        if (hasChapter) return null
        return (
            <WaitingText
                testID={testID}
                size="text-sm"
                weight="regular"
                color="text-muted"
                align="start"
                numberOfLines={2}
            >
                Proposition en cours…
            </WaitingText>
        )
    }

    if (analysis.status === "error") {
        if (hasChapter) return null
        return <AlertCard testID={testID} severity="warning" message={texts.suggestionUnavailable} />
    }

    if (analysis.selectedBySam === true && hasChapter) {
        return (
            <Row gap="2" align="center" testID={testID}>
                <Icon name="magic" size={16} color="primary" />
                <Text size="text-sm" color="text-muted">
                    Sélectionné par Sam
                </Text>
            </Row>
        )
    }

    if (hasChapter || analysis.readTitle == null || analysis.readTitle === "") return null

    const { crossClass } = analysis

    return (
        <Stack gap="3" testID={testID}>
            <AlertCard
                severity="info"
                message={
                    <Stack gap="1">
                        <Text size="text-sm" color="primary">
                            {`Sam a lu : « ${analysis.readTitle} »`}
                        </Text>
                        <Text size="text-sm" color="text-muted">
                            {scopeLabel != null && scopeLabel !== ""
                                ? `Pas de correspondance en ${scopeLabel}${texts.pickChapter}`
                                : `Pas de correspondance${texts.pickChapter}`}
                        </Text>
                        {analysis.crossClassLoading === true && (
                            <WaitingText
                                size="text-sm"
                                weight="regular"
                                color="text-muted"
                                align="start"
                                numberOfLines={2}
                            >
                                Recherche dans les autres classes…
                            </WaitingText>
                        )}
                    </Stack>
                }
            />

            {crossClass != null && (
                <AlertCard
                    severity="info"
                    message={
                        <Stack gap="2">
                            <Row gap="2" align="center">
                                <Icon name="globe" size={16} color="primary" />
                                <Text size="text-sm" color="primary">
                                    {`Ressemble à du ${crossClass.className}${
                                        crossClass.subjectName != null ? ` · ${crossClass.subjectName}` : ""
                                    }`}
                                </Text>
                            </Row>
                            <Text size="text-sm" color="text-muted">
                                {`« ${crossClass.chapterName} »${texts.switchClass}`}
                            </Text>
                            <Row gap="2" justify="end">
                                <Button label="Ignorer" variant="ghost" size="sm" onPress={onDismissCrossClass} />
                                <Button label="Basculer" size="sm" onPress={onAcceptCrossClass} />
                            </Row>
                        </Stack>
                    }
                />
            )}
        </Stack>
    )
}
